import React from 'react';
import { FieldArrayWithId } from 'react-hook-form';

import { CombinatorialOperator, InclusionOperator, OlmSelectivityCriterion, SelectivitySource } from '@/api/client';
import OlmSelectivityCriterionWithId from '@/components/message-details/selectivity/Selectivity.types';
import { OLM_FIELD_COMBINED_SELECTIVITY } from '@/features/selectivitySlice';
import { IOlmMessageForm } from '@/components/message-details/MessageDetails';
import SelectivityCriterionInfo from '@/components/message-details/selectivity/SelectivityCriterionInfo';
import { IDropdownItem } from '@/components/shared/Dropdown';

export const groupSelectivityCriteria = (
  criteria: OlmSelectivityCriterionWithId[],
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  fields: FieldArrayWithId<IOlmMessageForm, 'selectivityProfile.criteria', 'id'>[] | null = null,
): Array<OlmSelectivityCriterionWithId[]> => {
  const criteriasArray: Array<OlmSelectivityCriterionWithId[]> = [];
  let group: OlmSelectivityCriterionWithId[] = [];

  if (criteria && criteria.length) {
    const nrOfCriterias = criteria.length - 1;

    criteria.map((criterion, index) => {
      if (fields) {
        criterion.id = fields[index].id;
      }

      if (criterion.field && criterion.field.name?.toLowerCase() === OLM_FIELD_COMBINED_SELECTIVITY) {
        criterion.values = [];
      }

      if (criterion.field && criterion.isCombinedCriteriaParentRecord) {
        criterion.field.name = 'Combined Selectivity';
      }
      group.push(criterion);

      if (criterion.nextCriterionRelationship === CombinatorialOperator.Or || criterion.sequence === nrOfCriterias) {
        criteriasArray.push([...group]);
        group = [];
      }
    });

    return criteriasArray;
  }

  return [];
};

export const renderCriteriaGroupElements = (group: OlmSelectivityCriterionWithId[], isLastGroup = false) => {
  const tempGroup: JSX.Element[] = [];
  let newGroup: JSX.Element = <></>;
  let normalGroup: JSX.Element[] = [];
  let combinedSelectivityGroup: JSX.Element[] = [];
  let currentCombinedGroup = '';
  let currentCombinedKey = 0;

  group.map((criteria, index) => {
    if (criteria.combinedGroupCriteriaId && criteria.combinedGroupCriteriaId.length) {
      if (normalGroup.length) {
        tempGroup.push(<>{[...normalGroup]}</>);
        normalGroup = [];
      }
      currentCombinedGroup = criteria.combinedGroupCriteriaId;
      let lastIndexOfCombinedCriterion;
      let i = group.length;
      while (i >= 0 && !lastIndexOfCombinedCriterion) {
        if (group[i] && group[i].combinedGroupCriteriaId === currentCombinedGroup) {
          lastIndexOfCombinedCriterion = i;
        }
        i--;
      }
      if (
        (criteria.field &&
          criteria.field.name &&
          criteria.field?.name.toLowerCase() !== OLM_FIELD_COMBINED_SELECTIVITY) ||
        (!criteria.primaryKey && !criteria.isCombinedCriteriaParentRecord)
      ) {
        combinedSelectivityGroup.push(
          <SelectivityCriterionInfo
            key={`criterion-${criteria.primaryKey}`}
            criterion={criteria}
            isLast={isLastGroup && index === group.length - 1}
            isLastOfGroup={index === group.length - 1 || index === lastIndexOfCombinedCriterion}
          />,
        );
      }
      if (lastIndexOfCombinedCriterion === index) {
        tempGroup.push(
          <div
            className="combined-container"
            key={`combined-${currentCombinedKey}`}
            data-combId={`Combined Group ID: ${criteria.combinedGroupCriteriaId}`}
          >
            {[...combinedSelectivityGroup]}
          </div>,
        );
        combinedSelectivityGroup = [];
        currentCombinedKey++;
      }
    } else {
      normalGroup.push(
        <SelectivityCriterionInfo
          key={`criterion-${criteria.primaryKey}`}
          criterion={criteria}
          isLast={isLastGroup && index === group.length - 1}
          isLastOfGroup={index === group.length - 1}
        />,
      );
    }
  });

  if (normalGroup.length) {
    tempGroup.push(<>{[...normalGroup]}</>);
  }

  newGroup = <>{[...tempGroup]}</>;
  return newGroup;
};

export const selectivitySourceToInclusionOperation = (
  source: SelectivitySource | undefined,
  wheneverUnless: 'whenever' | 'unless',
) => {
  if (source || source === 0) {
    switch (source) {
      case SelectivitySource.MmSelectivityAndExternalFile:
        return wheneverUnless === 'whenever'
          ? InclusionOperator.WheneverSelectivityAndAnExternalFileMatches
          : InclusionOperator.UnlessSelectivityAndAnExternalFileMatches;
      case SelectivitySource.MmSelectivityOnly:
        return wheneverUnless === 'whenever'
          ? InclusionOperator.WheneverSelectivityMatches
          : InclusionOperator.UnlessSelectivityMatches;
      case SelectivitySource.MmSelectivityOrExternalFile:
        return wheneverUnless === 'whenever'
          ? InclusionOperator.WheneverSelectivityOrAnExternalFileMatches
          : InclusionOperator.UnlessSelectivityOrAnExternalFileMatches;
      case SelectivitySource.VantageQueryAndExternalFile:
        return InclusionOperator.WheneverAVantageQueryAndAnExternalFileMatches;
      case SelectivitySource.VantageQueryOnly:
        return InclusionOperator.WheneverAVantageQueryMatches;
      case SelectivitySource.ExternalFileOnly:
        return InclusionOperator.WheneverAnExternalFileMatches;
      default:
        return InclusionOperator.Always;
    }
  }
};

export const inclusionOperationToSelectivitySource: (inclusionOperator: InclusionOperator) => {
  source: SelectivitySource;
  wheneverUnless: 'whenever' | 'unless';
} = (inclusionOperation: InclusionOperator) => {
  switch (inclusionOperation) {
    case InclusionOperator.Always:
      return { source: SelectivitySource.Always, wheneverUnless: 'whenever' };
    case InclusionOperator.UnlessSelectivityAndAnExternalFileMatches:
      return { source: SelectivitySource.MmSelectivityAndExternalFile, wheneverUnless: 'unless' };
    case InclusionOperator.UnlessSelectivityMatches:
      return { source: SelectivitySource.MmSelectivityOnly, wheneverUnless: 'unless' };
    case InclusionOperator.UnlessSelectivityOrAnExternalFileMatches:
      return { source: SelectivitySource.MmSelectivityOrExternalFile, wheneverUnless: 'unless' };
    case InclusionOperator.WheneverAVantageQueryAndAnExternalFileMatches:
      return { source: SelectivitySource.VantageQueryAndExternalFile, wheneverUnless: 'whenever' };
    case InclusionOperator.WheneverAVantageQueryMatches:
      return { source: SelectivitySource.VantageQueryOnly, wheneverUnless: 'whenever' };
    case InclusionOperator.WheneverAnExternalFileMatches:
      return { source: SelectivitySource.ExternalFileOnly, wheneverUnless: 'whenever' };
    case InclusionOperator.WheneverSelectivityAndAnExternalFileMatches:
      return { source: SelectivitySource.MmSelectivityAndExternalFile, wheneverUnless: 'whenever' };
    case InclusionOperator.WheneverSelectivityMatches:
      return { source: SelectivitySource.MmSelectivityOnly, wheneverUnless: 'whenever' };
    case InclusionOperator.WheneverSelectivityOrAnExternalFileMatches:
      return { source: SelectivitySource.MmSelectivityOrExternalFile, wheneverUnless: 'whenever' };
    default:
      return { source: SelectivitySource.Always, wheneverUnless: 'whenever' };
  }
};

export const getLogicalBlockIdFromDB = (criterion: OlmSelectivityCriterion): string | undefined => {
  if (criterion.logicalBlockId !== null && criterion.logicalBlockId !== undefined && criterion.logicalBlockId !== 0) {
    if (criterion.logicalBlockId.toString().length === 1) return '000' + criterion.logicalBlockId.toString();
    if (criterion.logicalBlockId.toString().length === 2) return '00' + criterion.logicalBlockId.toString();
    if (criterion.logicalBlockId.toString().length === 3) return '0' + criterion.logicalBlockId.toString();
    return criterion.logicalBlockId.toString();
  }
};

export const sortDropdownItems = (unorderedItems: IDropdownItem[]): IDropdownItem[] => {
  let orderedItems: IDropdownItem[] = [];
  orderedItems = unorderedItems.sort((a, b) =>
    a.name && b.name ? (a.name > b.name ? 1 : b.name > a.name ? -1 : 0) : 0,
  );
  return orderedItems;
};
